import { fabric } from 'fabric';
import editorConfig from '~config/editorConfig';
import { isInSnapRange } from './isInSnapRange';
import { getState } from '~src/state';
import { calculateRelativeThresholds } from './calculateRelativeThresholds';

/**
 * SnapManager - Handles position snapping for objects
 *
 * Features:
 * - Object-to-object snapping
 * - Canvas center snapping
 * - Canvas edge snapping
 * - Guideline snapping
 *
 * Note: Visual alignment guides are handled by AlignmentManager
 */
export class SnapManager {
  constructor(canvas, options = {}) {
    this.canvas = canvas;

    // Mark that SnapManager is handling snapping
    window.__snapManagerInitialized = true;

    // Get base settings from config
    const baseSettings = {
      ...editorConfig.snapSettings,
      ...options,
    };

    // Store original options for recalculation on resize
    this._originalOptions = options;

    // Calculate relative thresholds based on canvas size
    this.options = calculateRelativeThresholds(canvas, baseSettings);

    this.cleanup = [];

    this._initializeEventHandlers();
  }

  _initializeEventHandlers() {
    // Direct object moving handler (no throttling for immediate snap)
    const handleObjectMoving = (e) => {
      this._handleObjectMoving(e);
    };

    this.canvas.on('object:moving', handleObjectMoving);
    this.cleanup.push(() =>
      this.canvas.off('object:moving', handleObjectMoving)
    );

    // Handle object scaling
    const handleObjectScaling = (e) => {
      this._handleObjectScaling(e);
    };

    this.canvas.on('object:scaling', handleObjectScaling);
    this.cleanup.push(() =>
      this.canvas.off('object:scaling', handleObjectScaling)
    );
  }

  _handleObjectMoving(e) {
    const activeObject = e.target;
    if (!activeObject) {
      return;
    }

    // Skip guidelines - they have their own snapping logic
    if (activeObject.isGuideline) {
      return;
    }

    // Check if snapping is enabled in settings
    const settings = getState('settings') || {};
    const snappingEnabled = settings.snappingEnabled !== false;

    const activeObjectBounds = this._getObjectBounds(activeObject);
    let snapCandidates = { x: null, y: null };
    let hasSnapped = false;

    // Check canvas center snapping
    const canvasCenterSnap = this._checkCanvasCenterSnap(activeObjectBounds);

    if (
      snappingEnabled &&
      this.options.enableCenterSnapping &&
      canvasCenterSnap.snapped
    ) {
      hasSnapped = true;
      if (canvasCenterSnap.x !== undefined)
        snapCandidates.x = canvasCenterSnap.x;
      if (canvasCenterSnap.y !== undefined)
        snapCandidates.y = canvasCenterSnap.y;
    }

    // Check canvas edge snapping
    const canvasEdgeSnap = this._checkCanvasEdgeSnap(activeObjectBounds);

    if (
      snappingEnabled &&
      this.options.enableCanvasEdgeSnapping &&
      canvasEdgeSnap.snapped
    ) {
      hasSnapped = true;
      if (canvasEdgeSnap.x !== undefined) snapCandidates.x = canvasEdgeSnap.x;
      if (canvasEdgeSnap.y !== undefined) snapCandidates.y = canvasEdgeSnap.y;
    }

    // Ruler mark snapping is disabled for regular objects
    // Only guidelines should snap to ruler marks

    // Always get nearby objects for alignment checking
    const nearbyObjects = this._getNearbyObjects(activeObject);

    // Check each nearby object for alignment and snapping
    for (const obj of nearbyObjects) {
      const snapResult = this._checkObjectSnap(activeObjectBounds, obj);

      // AlignmentManager handles visualization now

      // Only apply snap if object snapping is enabled
      if (
        snappingEnabled &&
        this.options.enableObjectSnapping &&
        snapResult.snapped
      ) {
        hasSnapped = true;
        // Store snap candidates but don't apply them yet
        if (snapResult.x !== undefined) snapCandidates.x = snapResult.x;
        if (snapResult.y !== undefined) snapCandidates.y = snapResult.y;
      }
    }

    // Apply snap position immediately during movement
    if (
      hasSnapped &&
      (snapCandidates.x !== null || snapCandidates.y !== null)
    ) {
      const currentCenter = activeObject.getCenterPoint();
      const finalX =
        snapCandidates.x !== null ? snapCandidates.x : currentCenter.x;
      const finalY =
        snapCandidates.y !== null ? snapCandidates.y : currentCenter.y;

      activeObject.setPositionByOrigin(
        new fabric.Point(finalX, finalY),
        'center',
        'center'
      );
      activeObject.setCoords();
    }
  }

  _getObjectBounds(obj) {
    const bounds = obj.getBoundingRect();
    const center = obj.getCenterPoint();

    // Use actual bounding rect dimensions (handles rotation correctly)
    // but use world coordinates for center
    return {
      left: center.x,
      top: center.y,
      width: bounds.width / this.canvas.getZoom(),
      height: bounds.height / this.canvas.getZoom(),
    };
  }

  _getNearbyObjects(activeObject) {
    const objects = this.canvas.getObjects();
    const nearby = [];

    for (const obj of objects) {
      if (obj === activeObject) continue;

      // Skip internal MagicFrame components
      if (obj._magicFrame || obj.excludeFromExport) continue;

      const bounds = this._getObjectBounds(obj);

      // Include all objects for alignment lines (no distance filtering)
      nearby.push({ obj, bounds });
    }

    return nearby;
  }

  _checkObjectSnap(activeBounds, targetData) {
    const { obj, bounds } = targetData;
    const result = {
      snapped: false,
    };

    // Handle guidelines
    if (obj.isGuideline) {
      return this._checkGuidelineSnap(activeBounds, obj);
    }

    // Check horizontal alignments
    const horizontalSnap = this._checkHorizontalSnap(activeBounds, bounds);
    if (horizontalSnap.snapped) {
      result.snapped = true;
      result.y = horizontalSnap.position;
    }

    // Check vertical alignments
    const verticalSnap = this._checkVerticalSnap(activeBounds, bounds);
    if (verticalSnap.snapped) {
      result.snapped = true;
      result.x = verticalSnap.position;
    }

    return result;
  }

  _checkGuidelineSnap(activeBounds, guideline) {
    const result = {
      snapped: false,
    };

    if (guideline.orientation === 'horizontal') {
      const guideY = guideline.top;
      const snapPoints = [
        {
          y: activeBounds.top - activeBounds.height / 2,
          target: guideY + activeBounds.height / 2,
        },
        { y: activeBounds.top, target: guideY },
        {
          y: activeBounds.top + activeBounds.height / 2,
          target: guideY - activeBounds.height / 2,
        },
      ];

      for (const point of snapPoints) {
        if (isInSnapRange(point.y, guideY, this.options.threshold)) {
          result.snapped = true;
          result.y = point.target;
          break;
        }
      }
    } else if (guideline.orientation === 'vertical') {
      const guideX = guideline.left;
      const snapPoints = [
        {
          x: activeBounds.left - activeBounds.width / 2,
          target: guideX + activeBounds.width / 2,
        },
        { x: activeBounds.left, target: guideX },
        {
          x: activeBounds.left + activeBounds.width / 2,
          target: guideX - activeBounds.width / 2,
        },
      ];

      for (const point of snapPoints) {
        if (isInSnapRange(point.x, guideX, this.options.threshold)) {
          result.snapped = true;
          result.x = point.target;
          break;
        }
      }
    }

    return result;
  }

  _checkCanvasCenterSnap(activeBounds) {
    const result = {
      snapped: false,
    };

    const canvasWidth = this.canvas.getWidth();
    const canvasHeight = this.canvas.getHeight();
    const canvasWidthCenter = canvasWidth / 2;
    const canvasHeightCenter = canvasHeight / 2;
    const threshold =
      this.options.centerLineSnapThreshold || this.options.threshold;

    // Check vertical center
    const verticalDistance = Math.abs(activeBounds.left - canvasWidthCenter);
    if (verticalDistance <= threshold) {
      result.snapped = true;
      result.x = canvasWidthCenter;
    }

    // Check horizontal center
    const horizontalDistance = Math.abs(activeBounds.top - canvasHeightCenter);
    if (horizontalDistance <= threshold) {
      result.snapped = true;
      result.y = canvasHeightCenter;
    }

    return result;
  }

  _checkCanvasEdgeSnap(activeBounds) {
    const result = {
      snapped: false,
    };

    const canvasWidth = this.canvas.getWidth();
    const canvasHeight = this.canvas.getHeight();
    const threshold =
      this.options.canvasEdgeSnapThreshold || this.options.threshold;

    // Check left edge
    const leftEdgeDistance = Math.abs(
      activeBounds.left - activeBounds.width / 2 - 0
    );
    if (leftEdgeDistance <= threshold) {
      result.snapped = true;
      result.x = activeBounds.width / 2;
    }

    // Check right edge
    const rightEdgeDistance = Math.abs(
      activeBounds.left + activeBounds.width / 2 - canvasWidth
    );
    if (rightEdgeDistance <= threshold) {
      result.snapped = true;
      result.x = canvasWidth - activeBounds.width / 2;
    }

    // Check top edge
    const topEdgeDistance = Math.abs(
      activeBounds.top - activeBounds.height / 2 - 0
    );
    if (topEdgeDistance <= threshold) {
      result.snapped = true;
      result.y = activeBounds.height / 2;
    }

    // Check bottom edge
    const bottomEdgeDistance = Math.abs(
      activeBounds.top + activeBounds.height / 2 - canvasHeight
    );
    if (bottomEdgeDistance <= threshold) {
      result.snapped = true;
      result.y = canvasHeight - activeBounds.height / 2;
    }

    return result;
  }

  // Ruler mark snapping method is no longer used for regular objects
  // Only guidelines snap to ruler marks (handled in ruler/eventHandlers.js)
  /*
  _checkRulerMarkSnap(activeBounds) {
    const result = {
      snapped: false,
    };

    // Check if rulers are enabled and get the ruler unit
    const rulerEnabled = getState('rulerEnabled', false);
    const rulerUnit = getState('rulerUnit', 'px');

    if (!rulerEnabled) {
      return result;
    }

    const markIntervals = getRulerMarkIntervals(rulerUnit);
    const threshold = this.options.threshold || 5;

    // Check horizontal ruler marks (for Y position)
    const horizontalSnapPoints = [
      { y: activeBounds.top - activeBounds.height / 2, type: 'top' },
      { y: activeBounds.top, type: 'center' },
      { y: activeBounds.top + activeBounds.height / 2, type: 'bottom' },
    ];

    for (const point of horizontalSnapPoints) {
      const rulerSnap = rulerMarkSnap(point.y, threshold, markIntervals);
      if (rulerSnap.snapped) {
        result.snapped = true;
        // Adjust position based on which edge is snapping
        if (point.type === 'top') {
          result.y = rulerSnap.position + activeBounds.height / 2;
        } else if (point.type === 'center') {
          result.y = rulerSnap.position;
        } else {
          // bottom
          result.y = rulerSnap.position - activeBounds.height / 2;
        }
        break;
      }
    }

    // Check vertical ruler marks (for X position)
    const verticalSnapPoints = [
      { x: activeBounds.left - activeBounds.width / 2, type: 'left' },
      { x: activeBounds.left, type: 'center' },
      { x: activeBounds.left + activeBounds.width / 2, type: 'right' },
    ];

    for (const point of verticalSnapPoints) {
      const rulerSnap = rulerMarkSnap(point.x, threshold, markIntervals);
      if (rulerSnap.snapped) {
        result.snapped = true;
        // Adjust position based on which edge is snapping
        if (point.type === 'left') {
          result.x = rulerSnap.position + activeBounds.width / 2;
        } else if (point.type === 'center') {
          result.x = rulerSnap.position;
        } else {
          // right
          result.x = rulerSnap.position - activeBounds.width / 2;
        }
        break;
      }
    }

    return result;
  }
  */

  _checkHorizontalSnap(activeBounds, targetBounds) {
    const snapPoints = [
      // Edge alignment
      {
        active: activeBounds.top - activeBounds.height / 2,
        target: targetBounds.top - targetBounds.height / 2,
        position:
          targetBounds.top - targetBounds.height / 2 + activeBounds.height / 2,
        type: 'top',
      },
      {
        active: activeBounds.top,
        target: targetBounds.top,
        position: targetBounds.top,
        type: 'center',
      },
      {
        active: activeBounds.top + activeBounds.height / 2,
        target: targetBounds.top + targetBounds.height / 2,
        position:
          targetBounds.top + targetBounds.height / 2 - activeBounds.height / 2,
        type: 'bottom',
      },
      // Edge touching - top of active to bottom of target
      {
        active: activeBounds.top - activeBounds.height / 2,
        target: targetBounds.top + targetBounds.height / 2,
        position:
          targetBounds.top + targetBounds.height / 2 + activeBounds.height / 2,
        type: 'top-to-bottom',
      },
      // Edge touching - bottom of active to top of target
      {
        active: activeBounds.top + activeBounds.height / 2,
        target: targetBounds.top - targetBounds.height / 2,
        position:
          targetBounds.top - targetBounds.height / 2 - activeBounds.height / 2,
        type: 'bottom-to-top',
      },
    ];

    const result = { snapped: false };

    for (const point of snapPoints) {
      const alignmentTolerance = this.options.alignmentTolerance || 30; // Show alignment lines when within configured pixels
      const distance = Math.abs(point.active - point.target);

      // Check if edges are nearly aligned (within tolerance for visual guides)
      if (distance <= alignmentTolerance) {
        // Check if within snap range for actual snapping
        const withinSnapRange = isInSnapRange(
          point.active,
          point.target,
          this.options.objectSnapThreshold || this.options.threshold
        );

        if (withinSnapRange) {
          result.snapped = true;
          result.position = point.position;
        }
      }
    }

    return result;
  }

  _checkVerticalSnap(activeBounds, targetBounds) {
    const snapPoints = [
      // Edge alignment
      {
        active: activeBounds.left - activeBounds.width / 2,
        target: targetBounds.left - targetBounds.width / 2,
        position:
          targetBounds.left - targetBounds.width / 2 + activeBounds.width / 2,
        type: 'left',
      },
      {
        active: activeBounds.left,
        target: targetBounds.left,
        position: targetBounds.left,
        type: 'center',
      },
      {
        active: activeBounds.left + activeBounds.width / 2,
        target: targetBounds.left + targetBounds.width / 2,
        position:
          targetBounds.left + targetBounds.width / 2 - activeBounds.width / 2,
        type: 'right',
      },
      // Edge touching - left of active to right of target
      {
        active: activeBounds.left - activeBounds.width / 2,
        target: targetBounds.left + targetBounds.width / 2,
        position:
          targetBounds.left + targetBounds.width / 2 + activeBounds.width / 2,
        type: 'left-to-right',
      },
      // Edge touching - right of active to left of target
      {
        active: activeBounds.left + activeBounds.width / 2,
        target: targetBounds.left - targetBounds.width / 2,
        position:
          targetBounds.left - targetBounds.width / 2 - activeBounds.width / 2,
        type: 'right-to-left',
      },
    ];

    const result = { snapped: false };

    for (const point of snapPoints) {
      const alignmentTolerance = this.options.alignmentTolerance || 30; // Show alignment lines when within configured pixels
      const distance = Math.abs(point.active - point.target);

      // Check if edges are nearly aligned (within tolerance for visual guides)
      if (distance <= alignmentTolerance) {
        // Check if within snap range for actual snapping
        const withinSnapRange = isInSnapRange(
          point.active,
          point.target,
          this.options.objectSnapThreshold || this.options.threshold
        );

        if (withinSnapRange) {
          result.snapped = true;
          result.position = point.position;
        }
      }
    }

    return result;
  }

  _handleObjectScaling() {
    // Scaling snap is disabled - alignment lines are handled by AlignmentManager
    // This prevents the problematic snap behavior during scaling
    return;
  }

  /**
   * Update snap settings
   */
  updateOptions(options) {
    // Update original options
    this._originalOptions = { ...this._originalOptions, ...options };

    // Get base settings with new options
    const baseSettings = {
      ...editorConfig.snapSettings,
      ...this._originalOptions,
    };

    // Recalculate thresholds with new options
    this.options = calculateRelativeThresholds(this.canvas, baseSettings);
  }

  /**
   * Clean up all event listeners and resources
   */
  destroy() {
    try {
      this.cleanup.forEach((fn) => fn());
      this.cleanup = [];

      // Clear the initialization flag
      window.__snapManagerInitialized = false;
    } catch (error) {
      console.error('Error destroying SnapManager:', error);
    }
  }
}
